<template>
    <div>

        <PageHeader>
            {{ $t('dashboard.title') }}
            <template #side>
                <div class="d-flex gap-3">
                    <ButtonIcon icon="refresh-cw" variant="link" :disabled="loading" :icon-animation="iconAnimation" @click="reload" />
                    <TimeRangeCalendar v-model="timeRange" compare-year :disabled="loading" />
                </div>
            </template>
        </PageHeader>

        <div class="d-flex flex-column gap-3">
            <div class="d-flex flex-wrap gap-3">
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.impressions')"
                    :value="$format.number(impressions.impressions)"
                    :difference="$format.percent(percentDiff(impressions.impressions, impressions.impressions_compare))"
                    :loading="requestImpressionsStats.getLoading()"
                    :error="requestImpressionsStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.impressionsTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.clicks')"
                    :value="$format.number(clicks.clicks)"
                    :difference="$format.percent(percentDiff(clicks.clicks, clicks.clicks_compare))"
                    :loading="requestClicksStats.getLoading()"
                    :error="requestClicksStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.clicksTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.ctr')"
                    :value="$format.percent(clicks.clicks / impressions.impressions * 100)"
                    :difference="$format.percent(percentDiff(clicks.clicks / impressions.impressions * 100, clicks.clicks_compare / impressions.impressions_compare * 100))"
                    :loading="requestClicksStats.getLoading() || requestImpressionsStats.getLoading()"
                    :error="requestClicksStats.getError() || requestImpressionsStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.ctrTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.transactions')"
                    :value="$format.number(orders.orders)"
                    :difference="$format.percent(percentDiff(orders.orders, orders.orders_compare))"
                    :loading="requestOrdersStats.getLoading()"
                    :error="requestOrdersStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.transactionsTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.cr')"
                    :value="$format.percent(orders.orders / clicks.clicks * 100)"
                    :difference="$format.percent(percentDiff(orders.orders / clicks.clicks * 100, orders.orders_compare / clicks.clicks_compare * 100))"
                    :loading="requestOrdersStats.getLoading() || requestClicksStats.getLoading()"
                    :error="requestOrdersStats.getError() || requestClicksStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.crTooltip')"
                />
            </div>
            <div class="d-flex flex-wrap gap-3">
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.quantity')"
                    :value="$format.number(sales.quantity)"
                    :difference="$format.percent(percentDiff(sales.quantity, sales.quantity_compare))"
                    :loading="requestSalesStats.getLoading()"
                    :error="requestSalesStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.quantityTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.salesAmountOwn')"
                    :value="$format.currency(sales.amount)"
                    :difference="$format.percent(percentDiff(sales.amount, sales.amount_compare))"
                    :loading="requestSalesStats.getLoading()"
                    :error="requestSalesStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.salesAmountOwnTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.amountOther')"
                    :value="$format.currency(sales.amount_other)"
                    :difference="$format.percent(percentDiff(sales.amount_other, sales.amount_other_compare))"
                    :loading="requestSalesStats.getLoading()"
                    :error="requestSalesStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.amountOtherTooltip')"
                />
                <WidgetStatChart
                    class="flex-grow-1 width-min-200 flex-basis-0 h-auto"
                    :title="$t('dashboard.amountUnknown')"
                    :value="$format.currency(sales.amount_unknown)"
                    :difference="$format.percent(percentDiff(sales.amount_unknown, sales.amount_unknown_compare))"
                    :loading="requestSalesStats.getLoading()"
                    :error="requestSalesStats.getError()"
                    tooltip-formatter="round"
                    :title-tooltip="$t('dashboard.amountUnknownTooltip')"
                />
            </div>
        </div>

        <EChart
            card
            class="mt-3"
            :options="trafficChartOptions"
            :header="$t('dashboard.traffic')"
            :error="requestTrafficChart.getError()"
            :loading="requestTrafficChart.getLoading()"
            type="time"
        >
            <template #nav>
                <MultiselectAvailableCampaigns v-model="campaigns" :disabled="requestTrafficChart.getLoading()" @change="fetchTrafficChart()" />
            </template>
        </EChart>

        <EChart
            card
            class="mt-3"
            :options="salesChartOptions"
            :header="$t('dashboard.sales')"
            :error="requestSalesChart.getError()"
            :loading="requestSalesChart.getLoading()"
            type="time"
        />
    </div>
</template>



<script>
    import DashboardMixin from '@/views/common/dashboard/index.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import { generateEChartData } from '@/utils/chart';
    import EChart from '@/components/common/EChart.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';

    export default {
        name: 'ProducerDashboard',
        mixins: [DashboardMixin],
        components: {
            TimeRangeCalendar,
            EChart,
            MultiselectAvailableCampaigns,
        },
        data() {
            return {
                campaigns: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loading() {
                return (
                    this.requestImpressionsStats.getLoading() ||
                    this.requestClicksStats.getLoading() ||
                    this.requestOrdersStats.getLoading() ||
                    this.requestSalesStats.getLoading() ||
                    this.requestTrafficChart.getLoading() ||
                    this.requestSalesChart.getLoading()
                );
            },
            iconAnimation() {
                return this.loading ? 'spin' : undefined;
            },
            chartOverview() {
                return {};
            },
            trafficChartOptions() {
                const chartData = this.trafficChart.reduce(
                    (acc, item) => {
                        acc.impressions.push([item.date, item.impressions]);
                        acc.clicks.push([item.date, item.clicks]);
                        return acc;
                    },
                    { impressions: [], clicks: [] }
                );

                return {
                    yAxis: [
                        {
                            type: 'value',
                            name: this.$t('dashboard.clicks'),
                        },
                        {
                            type: 'value',
                            name: this.$t('dashboard.offersDisplay'),
                            position: 'right',
                        },
                    ],
                    series: [
                        {
                            name: this.$t('dashboard.clicks'),
                            type: 'line',
                            data: generateEChartData(this.timeRange, chartData.clicks, this.groupBy),
                        },
                        {
                            name: this.$t('dashboard.offersDisplay'),
                            type: 'bar',
                            yAxisIndex: 1,
                            data: generateEChartData(this.timeRange, chartData.impressions, this.groupBy),
                        },
                    ],
                };
            },
            salesChartOptions() {
                const chartData = this.salesChart.reduce(
                    (acc, item) => {
                        acc.amount.push([item.date, item.amount]);
                        acc.quantity.push([item.date, item.quantity]);
                        return acc;
                    },
                    { amount: [], quantity: [] }
                );

                return {
                    yAxis: [
                        {
                            type: 'value',
                            name: this.$t('dashboard.quantity'),
                        },
                        {
                            type: 'value',
                            name: this.$t('dashboard.salesAmount'),
                            position: 'right',
                        },
                    ],
                    series: [
                        {
                            name: this.$t('dashboard.quantity'),
                            type: 'line',
                            data: generateEChartData(this.timeRange, chartData.quantity, this.groupBy),
                        },
                        {
                            name: this.$t('dashboard.salesAmount'),
                            type: 'bar',
                            yAxisIndex: 1,
                            data: generateEChartData(this.timeRange, chartData.amount, this.groupBy),
                            tooltip: {
                                valueFormatter: value => this.$format.currency(value),
                            },
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId: {
                handler: 'fetchData',
            },
        },
        methods: {
            fetchData(params = {}) {
                this.fetchImpressionsStats(params);
                this.fetchClicksStats(params);
                this.fetchOrdersStats(params);
                this.fetchSalesStats(params);
                this.fetchTrafficChart(params);
                this.fetchSalesChart(params);
            },
            reload() {
                const params = { cache: false };
                this.fetchData(params);
            },
            fetchTrafficChart(params = {}) {
                const { endDate, startDate } = this.apiParams.requestParams;
                const apiParams = {
                    requestParams: { endDate, startDate },
                    ...params,
                };
                if (this.campaigns?.length) apiParams.requestParams.campaigns = this.campaigns.map(item => item.id).join(',');
                this.requestTrafficChart.get(apiParams);
            },
            fetchSalesChart(params = {}) {
                const { endDate, startDate } = this.apiParams.requestParams;
                const apiParams = {
                    requestParams: { endDate, startDate },
                    ...params,
                };
                this.requestSalesChart.get(apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
        },
    };
</script>
